import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchAllBookings } from '../services/bookingService';
import BookingsTable from '../components/BookingsTable';
import Header from '../components/Header';
import HeaderTitle from '../components/HeaderTitle';
import { NavbarToggle } from '../components/Navbar';
import { useSidebar } from '../layouts/Dashboard';
import { exportBookingsToExcel } from '../utils/excelExport';
import { logout } from '../services/authService';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { toggleSidebar } = useSidebar();
  const history = useHistory();

  const handleLogout = async () => {
    try {
      await logout();
      history.push('/login');
    } catch (error) {
      console.error('Logout error:', error);
      history.push('/login');
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllBookings();
      setBookings(data);
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      await exportBookingsToExcel();
      alert('Bookings exported to Excel successfully!');
    } catch (error) {
      alert(`Failed to export bookings: ${error.message}`);
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={loadBookings} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
      'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    const dayName = dayNames[now.getDay()];
    
    return { day, month, year, dayName };
  };

  const { day, month, year, dayName } = formatDate();

  return (
    <div className="admin-dashboard">
      <Header>
        <div className="admin-topbar">
          <div>
            <div className="effektToggle">
              <NavbarToggle onToggle={toggleSidebar} />
              <HeaderTitle>Bookings</HeaderTitle>
            </div>
            <div className="mt-3">
              <div className="d-flex align-items-start">
                <div className="fancy-date-display">
                  <div className="current-date">
                    {day}
                  </div>
                  <div className="current-month-year">
                    {month} {year}
                  </div>
                  <div className="current-day">
                    {dayName}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="admin-topbar-actions">
            <button 
              className="logout-button" 
              onClick={handleLogout}
              type="button"
            >
              Logout
            </button>
          </div>
        </div>
      </Header>

      <div className="dashboard-stats">
        <div className="stat-card stat-card-blue">
          <div className="stat-card-wave">
            <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="none">
              <path
                d="M0,200 Q75,150 150,180 T300,160 L300,200 Z"
                fill="#4b9fff"
                opacity="0.1"
              />
              <path
                d="M0,200 Q75,170 150,190 T300,170 L300,200 Z"
                fill="#4b9fff"
                opacity="0.05"
              />
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-left">
              <div className="stat-label">Total Bookings</div>
              <div className="stat-value">{bookings.length.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="stat-card stat-card-red">
          <div className="stat-card-wave">
            <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="none">
              <path
                d="M0,200 Q75,150 150,180 T300,160 L300,200 Z"
                fill="#ff7a7a"
                opacity="0.1"
              />
              <path
                d="M0,200 Q75,170 150,190 T300,170 L300,200 Z"
                fill="#ff7a7a"
                opacity="0.05"
              />
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-left">
              <div className="stat-label">Booking Made</div>
              <div className="stat-value">{bookings.filter(b => b.status === 'booking_made').length.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="stat-card stat-card-green">
          <div className="stat-card-wave">
            <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="none">
              <path
                d="M0,200 Q75,150 150,180 T300,160 L300,200 Z"
                fill="#00d4a6"
                opacity="0.1"
              />
              <path
                d="M0,200 Q75,170 150,190 T300,170 L300,200 Z"
                fill="#00d4a6"
                opacity="0.05"
              />
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-left">
              <div className="stat-label">Paid</div>
              <div className="stat-value">{bookings.filter(b => b.payment_status === 'paid').length.toLocaleString()}</div>
            </div>
          </div>
        </div>
        <div className="stat-card stat-card-purple">
          <div className="stat-card-wave">
            <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="none">
              <path
                d="M0,200 Q75,150 150,180 T300,160 L300,200 Z"
                fill="#845ef7"
                opacity="0.1"
              />
              <path
                d="M0,200 Q75,170 150,190 T300,170 L300,200 Z"
                fill="#845ef7"
                opacity="0.05"
              />
            </svg>
          </div>
          <div className="stat-card-content">
            <div className="stat-card-left">
              <div className="stat-label">Cancelled</div>
              <div className="stat-value">{bookings.filter(b => b.status === 'canceled').length.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bookings-section">
        <div className="bookings-toolbar">
          <h2 className="mb-0">All Bookings</h2>
          <div className="action-buttons">
            <button 
              onClick={handleExportToExcel} 
              className="export-button"
              disabled={exporting || bookings.length === 0}
            >
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
            <button onClick={loadBookings} className="refresh-button">
              Refresh
            </button>
          </div>
        </div>
        <BookingsTable bookings={bookings} onUpdate={loadBookings} />
      </div>
    </div>
  );
};

export default AdminDashboard;
